import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, PenLine } from "lucide-react";
import { api } from "@/lib/api";

function Stars5({ value, size = 14 }) {
  return (
    <span className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= value ? "fill-[#f8b818] text-[#f8b818]" : "text-[#241c0d]/20"} />
      ))}
    </span>
  );
}

export default function ReviewsHighlight() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews")
      .then((res) => setReviews((res.data || []).slice(0, 3)))
      .catch(() => setReviews([]));
  }, []);

  return (
    <section className="border-y border-[#241c0d]/10 bg-[#fff1d9] py-24" data-testid="home-reviews-strip">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="eyebrow">Word on the track</p>
            <h2 className="mt-3 max-w-2xl text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>FAMILIES LOVE IT HERE</h2>
          </div>
          <Link to="/reviews" className="btn-outline !px-5 !py-2.5 !text-xs" data-testid="home-reviews-all-btn">
            Read all reviews <ArrowRight size={14} />
          </Link>
        </motion.div>

        {reviews.length === 0 ? (
          <div className="card-dark mt-12 p-12 text-center" data-testid="home-reviews-empty">
            <p className="font-display text-2xl text-[#c87700]">BE THE FIRST TO TELL THE STORY</p>
            <p className="mt-2 text-sm text-[#8a7b5e]">Ridden the track? Share your visit with other families.</p>
            <Link to="/reviews" className="btn-gold mt-6 inline-flex" data-testid="home-reviews-write-btn">
              <PenLine size={15} /> Write a review
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {reviews.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="card-dark flex flex-col p-6"
                data-testid={`home-review-card-${i}`}
              >
                <Stars5 value={r.rating || 5} />
                <p className="mt-4 flex-1 text-base font-semibold leading-relaxed text-[#241c0d] sm:text-lg line-clamp-5" data-testid={`home-review-text-${i}`}>
                  "{r.text}"
                </p>
                <div className="mt-5 flex items-center gap-3 border-t border-[#241c0d]/10 pt-4">
                  {r.avatar_url ? (
                    <img
                      src={r.avatar_url}
                      alt={r.name}
                      className="h-9 w-9 shrink-0 rounded-full object-cover"
                      data-testid={`home-review-avatar-img-${i}`}
                    />
                  ) : (
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f8b818]/20 font-display text-sm text-[#c87700]"
                      data-testid={`home-review-avatar-${i}`}
                    >
                      {(r.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-extrabold">{r.name}</p>
                    {r.source && <p className="text-xs font-bold uppercase tracking-widest text-[#8a7b5e]">{r.source}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
